/**
 * The shared report filter surface.
 *
 * Presentational and domain-blind: it does not know what a sport is, what a
 * scope is, or that a range has two ends. It owns one thing — the contract that
 * a filtered view must STATE what it is filtered by.
 *
 * That contract is why the active-chip strip is part of the component and not
 * an optional extra. The moment a bar collapses its rarely-touched controls
 * behind a "Filters (3)" button — which it must, or the bar wraps to three rows
 * on a laptop — the chip strip becomes the only place the complete active set
 * is written down. A count badge alone tells you that something is filtering
 * your numbers without telling you what.
 */

import type { ReactNode } from 'react'
import { Button } from './core'
import { Icon } from './Icon'

export interface ActiveFilter {
  id: string
  /** What is on, e.g. "Sport: Cricket" or "Members: 3 selected". */
  label: ReactNode
  /** Accessible name of the × — "Remove the Cricket sport filter". */
  removeLabel: string
  onRemove: () => void
}

export interface FilterBarProps {
  /** The always-inline controls, left to right. */
  children: ReactNode
  /** Right-aligned trailing content — provenance, export. */
  actions?: ReactNode
  /** Everything currently narrowing the view. Empty renders no second row. */
  active?: ActiveFilter[]
  onClearAll?: () => void
  clearLabel?: string
  /** Names the region. */
  label?: string
  /** sr-only live line, e.g. "Showing 12 events · filtered by sport". */
  status?: ReactNode
}

export function FilterBar({
  children,
  actions,
  active = [],
  onClearAll,
  clearLabel = 'Clear all',
  label = 'Filters',
  status,
}: FilterBarProps) {
  return (
    <div className="w7-filterbar" role="region" aria-label={label}>
      <div className="w7-filterbar-row">
        {children}
        {actions ? (
          <>
            <span className="w7-spacer" />
            {actions}
          </>
        ) : null}
      </div>
      {active.length > 0 ? (
        <div className="w7-filterbar-chips">
          {active.map((filter) => (
            <span key={filter.id} className="w7-activechip">
              <span className="w7-truncate">{filter.label}</span>
              <button type="button" aria-label={filter.removeLabel} title={filter.removeLabel} onClick={filter.onRemove}>
                <Icon name="close" size={12} />
              </button>
            </span>
          ))}
          {onClearAll ? (
            <Button variant="ghost" size="sm" icon="close" onClick={onClearAll}>
              {clearLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
      {status ? (
        <span className="w7-sr" aria-live="polite">
          {status}
        </span>
      ) : null}
    </div>
  )
}
